import React, { useEffect, useState } from "react";
import Breadcrumb from "../../components/Breadcrumb";
import MessageDialog from "../../components/MessageDialog";
import StorageService from "../../services/StorageService";
import CompanyService from "../../services/companyService";
import ProjectService from "../../services/ProjectService";
import ProjectStoreService from "../../services/ProjectStoreService";
import ItemCategoryService from "../../services/ItemCategoryService";
import ItemService from "../../services/ItemsServices";
import StockTransferReportService from "../../services/stockTransferReportService";
import utility from "../../utils/utility";
import XLSX from "xlsx-js-style";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import logo from "../../assets/images/deviops_logo.png";
import { addPdfLogo, getPdfLogoDataUrl } from "../../utils/pdfLogo";

export default function StockTransferReport() {
  const activeCompany = StorageService.activeCompany();
  const companyId = activeCompany?.companyId || "";
  const loginData = StorageService.loginData();
  const employeeId = loginData?.employeeId || "";
  const roleId = loginData?.roleId || "";

  // Mode state: TRANSFER (Outward / Sent) vs RECEIVE (Inward / Received)
  const [reportMode, setReportMode] = useState("TRANSFER");

  // Filter states
  const [status, setStatus] = useState("ALL"); // ALL, intransit, received, cancelled, rejected
  const [transferType, setTransferType] = useState(""); // ALL (empty), INTERNAL, COMPANY
  const [fromCompanyId, setFromCompanyId] = useState(companyId);
  const [fromProjectId, setFromProjectId] = useState("");
  const [fromStoreId, setFromStoreId] = useState("");
  const [itemCategoryId, setItemCategoryId] = useState("");
  const [itemId, setItemId] = useState("");
  const [toCompanyId, setToCompanyId] = useState("");
  const [toProjectId, setToProjectId] = useState("");
  const [toStoreId, setToStoreId] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  // Dropdown lists
  const [companies, setCompanies] = useState([]);
  const [fromProjects, setFromProjects] = useState([]);
  const [fromStores, setFromStores] = useState([]);
  const [toProjects, setToProjects] = useState([]);
  const [toStores, setToStores] = useState([]);
  const [itemCategories, setItemCategories] = useState([]);
  const [items, setItems] = useState([]);

  // Data states
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [msgType, setMsgType] = useState("");

  // Pagination states
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [totalPages, setTotalPages] = useState(0);
  const [totalRecords, setTotalRecords] = useState(0);

  const showMessage = (text, type = "error") => {
    setMessage(text);
    setMsgType(type);
  };

  const formatProjectDisplay = (project) => {
    const safeProject = typeof project === "string"
      ? { projectName: project }
      : (project || {});

    if (!safeProject) return "-";

    const projectName = safeProject.projectName || safeProject.name || "";
    const categoryName = safeProject.projectCategoryName || safeProject.categoryName || safeProject.category || "";
    const projectCode = safeProject.projectCode || "";
    const categoryInitial = String(categoryName).trim().charAt(0).toUpperCase();

    if (projectName) {
      if (categoryInitial) {
        return `${categoryInitial}*${projectName}`;
      }

      if (projectCode) {
        const codeInitial = String(projectCode).trim().charAt(0).toUpperCase();
        return `${codeInitial}*${projectName}`;
      }

      return projectName;
    }

    if (projectCode) {
      const codeInitial = String(projectCode).trim().charAt(0).toUpperCase();
      return `${codeInitial}*`;
    }

    return categoryInitial ? `${categoryInitial}*` : "-";
  };

  // Load initial companies and categories
  const loadDropdowns = async () => {
    try {
      const companyRes = await CompanyService.getActiveCompanies();
      setCompanies(companyRes?.data || companyRes || []);

      const categoryRes = await ItemCategoryService.getActive();
      setItemCategories(categoryRes?.data || categoryRes || []);
    } catch (error) {
      console.error("Error loading initial dropdowns:", error);
    }
  };

  // Load From Projects
  const loadFromProjects = async (targetCompanyId) => {
    if (!targetCompanyId) {
      setFromProjects([]);
      return;
    }
    try {
      let projectsRes = [];
      if (reportMode === "TRANSFER" && Number(roleId) !== 1 && Number(roleId) !== 2 && String(targetCompanyId) === String(companyId)) {
        const res = await ProjectService.getActiveProjectsByEmployee(targetCompanyId, employeeId);
        projectsRes = res?.data || (Array.isArray(res) ? res : res?.data?.data || []);
      } else {
        const res = await ProjectService.getActive(targetCompanyId);
        projectsRes = Array.isArray(res) ? res : (res?.data || []);
      }
      setFromProjects(projectsRes);
    } catch (err) {
      console.error("Error loading from projects:", err);
      setFromProjects([]);
    }
  };

  // Load To Projects
  const loadToProjects = async (targetCompanyId) => {
    if (!targetCompanyId) {
      setToProjects([]);
      return;
    }
    try {
      let projectsRes = [];
      if (reportMode === "RECEIVE" && Number(roleId) !== 1 && Number(roleId) !== 2 && String(targetCompanyId) === String(companyId)) {
        const res = await ProjectService.getActiveProjectsByEmployee(targetCompanyId, employeeId);
        projectsRes = res?.data || (Array.isArray(res) ? res : res?.data?.data || []);
      } else {
        const res = await ProjectService.getActive(targetCompanyId);
        projectsRes = Array.isArray(res) ? res : (res?.data || []);
      }
      setToProjects(projectsRes);
    } catch (err) {
      console.error("Error loading to projects:", err);
      setToProjects([]);
    }
  };

  // Load Stores
  const loadStores = async (projectId, type = "FROM") => {
    if (!projectId) {
      if (type === "FROM") setFromStores([]);
      else setToStores([]);
      return;
    }
    try {
      const res = await ProjectStoreService.getProjectStoreById(projectId);
      if (type === "FROM") {
        setFromStores(res?.data || res || []);
      } else {
        setToStores(res?.data || res || []);
      }
    } catch (err) {
      console.error("Store load error:", err);
      if (type === "FROM") setFromStores([]);
      else setToStores([]);
    }
  };

  // Load Items by Category
  const loadItems = async (categoryId) => {
    if (!categoryId) {
      setItems([]);
      return;
    }
    try {
      const res = await ItemService.getItemByCategoryId(categoryId);
      setItems(res?.data || []);
    } catch (err) {
      console.error("Error loading items by category:", err);
      setItems([]);
    }
  };

  // Switch reportMode handler
  const handleReportModeChange = (newMode) => {
    if (newMode === reportMode) return;
    setReportMode(newMode);
    setTransferType("");
    setStatus("ALL");
    setFromProjectId("");
    setFromStoreId("");
    setToProjectId("");
    setToStoreId("");
    setItemCategoryId("");
    setItemId("");
    setPage(1);

    if (newMode === "RECEIVE") {
      setToCompanyId(companyId);
      setFromCompanyId("");
      loadToProjects(companyId);
      setFromProjects([]);
    } else {
      setFromCompanyId(companyId);
      setToCompanyId("");
      loadFromProjects(companyId);
      setToProjects([]);
    }
  };

  // Handle fromCompanyId change
  useEffect(() => {
    if (fromCompanyId) {
      loadFromProjects(fromCompanyId);
      setFromProjectId("");
      setFromStoreId("");
    } else {
      setFromProjects([]);
      setFromProjectId("");
      setFromStoreId("");
    }
  }, [fromCompanyId, reportMode]);

  // Handle fromProjectId change
  useEffect(() => {
    if (fromProjectId) {
      loadStores(fromProjectId, "FROM");
      setFromStoreId("");

      if (reportMode === "TRANSFER") {
        const selectedProject = fromProjects.find(
          (p) => String(p.id || p.projectId) === String(fromProjectId)
        );
        if (selectedProject?.projectStartDate) {
          const formattedDate = new Date(selectedProject.projectStartDate)
            .toISOString()
            .split("T")[0];
          setStartDate(formattedDate);
        } else {
          setStartDate("");
        }
      }
    } else {
      setFromStores([]);
      if (reportMode === "TRANSFER") {
        setStartDate("");
      }
    }
  }, [fromProjectId, fromProjects, reportMode]);

  // Handle toCompanyId change
  useEffect(() => {
    if (toCompanyId) {
      loadToProjects(toCompanyId);
      setToProjectId("");
      setToStoreId("");
    } else {
      setToProjects([]);
      setToProjectId("");
      setToStoreId("");
    }
  }, [toCompanyId, reportMode]);

  // Handle toProjectId change
  useEffect(() => {
    if (toProjectId) {
      loadStores(toProjectId, "TO");
      setToStoreId("");

      if (reportMode === "RECEIVE") {
        const selectedProject = toProjects.find(
          (p) => String(p.id || p.projectId) === String(toProjectId)
        );
        if (selectedProject?.projectStartDate) {
          const formattedDate = new Date(selectedProject.projectStartDate)
            .toISOString()
            .split("T")[0];
          setStartDate(formattedDate);
        } else {
          setStartDate("");
        }
      }
    } else {
      setToStores([]);
      if (reportMode === "RECEIVE") {
        setStartDate("");
      }
    }
  }, [toProjectId, toProjects, reportMode]);

  // Handle transferType change
  useEffect(() => {
    if (reportMode === "TRANSFER") {
      if (transferType === "INTERNAL") {
        setToCompanyId(fromCompanyId || companyId);
      } else if (transferType === "COMPANY") {
        if (String(toCompanyId) === String(fromCompanyId || companyId)) {
          setToCompanyId("");
        }
      }
    } else {
      // RECEIVE mode
      if (transferType === "INTERNAL") {
        setFromCompanyId(toCompanyId || companyId);
      } else if (transferType === "COMPANY") {
        if (String(fromCompanyId) === String(toCompanyId || companyId)) {
          setFromCompanyId("");
        }
      }
    }
  }, [transferType, reportMode, fromCompanyId, toCompanyId]);

  // Handle itemCategoryId change
  useEffect(() => {
    if (itemCategoryId) {
      loadItems(itemCategoryId);
      setItemId("");
    } else {
      setItems([]);
    }
  }, [itemCategoryId]);

  // Load initial data
  useEffect(() => {
    loadDropdowns();
    if (companyId) {
      const today = new Date();
      setEndDate(today.toISOString().split("T")[0]);
      if (reportMode === "TRANSFER") {
        setFromCompanyId(companyId);
        loadFromProjects(companyId);
      } else {
        setToCompanyId(companyId);
        loadToProjects(companyId);
      }
      loadReport(1);
    }
  }, [companyId]);

  const handleClearFilters = () => {
    setTransferType("");
    setStatus("ALL");
    setFromProjectId("");
    setFromStoreId("");
    setToProjectId("");
    setToStoreId("");
    setItemCategoryId("");
    setItemId("");
    setStartDate("");
    const today = new Date();
    setEndDate(today.toISOString().split("T")[0]);
    if (reportMode === "TRANSFER") {
      setFromCompanyId(companyId);
      setToCompanyId("");
      loadFromProjects(companyId);
      setToProjects([]);
    } else {
      setToCompanyId(companyId);
      setFromCompanyId("");
      loadToProjects(companyId);
      setFromProjects([]);
    }
    setReports([]);
    setTotalRecords(0);
    setTotalPages(0);
    setPage(1);
  };

  // Fetch Report Data
  const loadReport = async (pageNumber = page, overrideMode = reportMode) => {
    if (startDate && !endDate) {
      showMessage("From Date and To Date are required");
      return;
    }
    if (startDate && endDate && new Date(startDate) > new Date(endDate)) {
      showMessage("From Date should be less than or equal to To Date");
      return;
    }

    setLoading(true);
    try {
      const res = await StockTransferReportService.getReport({
        reportMode: overrideMode,
        status: status !== "ALL" ? status : undefined,
        companyId: companyId || undefined,
        employeeId: employeeId || undefined,
        roleId: roleId || undefined,
        transferType: transferType || undefined,
        fromCompanyId: fromCompanyId || undefined,
        fromProjectId: fromProjectId || undefined,
        fromStoreId: fromStoreId || undefined,
        itemCategoryId: itemCategoryId || undefined,
        itemId: itemId || undefined,
        toCompanyId: toCompanyId || undefined,
        toProjectId: toProjectId || undefined,
        toStoreId: toStoreId || undefined,
        startDate: startDate || undefined,
        endDate: endDate || undefined,
        page: pageNumber,
        limit: limit,
      });

      setReports(res?.data || []);
      setTotalPages(res?.pagination?.totalPages || 0);
      setTotalRecords(res?.pagination?.total || 0);
      setPage(pageNumber);
    } catch (error) {
      console.error("Error fetching stock transfer report:", error);
      setReports([]);
      showMessage("Error fetching stock transfer report");
    } finally {
      setLoading(false);
    }
  };

  const getAllReportData = async () => {
    try {
      let allData = [];
      let currentPage = 1;
      let totalPagesCount = 1;

      do {
        const res = await StockTransferReportService.getReport({
          reportMode: reportMode,
          status: status !== "ALL" ? status : undefined,
          companyId: companyId || undefined,
          employeeId: employeeId || undefined,
          roleId: roleId || undefined,
          transferType: transferType || undefined,
          fromCompanyId: fromCompanyId || undefined,
          fromProjectId: fromProjectId || undefined,
          fromStoreId: fromStoreId || undefined,
          itemCategoryId: itemCategoryId || undefined,
          itemId: itemId || undefined,
          toCompanyId: toCompanyId || undefined,
          toProjectId: toProjectId || undefined,
          toStoreId: toStoreId || undefined,
          startDate: startDate || undefined,
          endDate: endDate || undefined,
          page: currentPage,
          limit: 100,
        });

        const data = res?.data || [];
        const pagination = res?.pagination || {};
        allData = [...allData, ...data];
        totalPagesCount = pagination.totalPages || 1;
        currentPage++;
      } while (currentPage <= totalPagesCount);

      return allData;
    } catch (error) {
      console.error("Error exporting data:", error);
      return [];
    }
  };

  const exportExcel = async () => {
    const allData = await getAllReportData();
    if (!allData.length) {
      showMessage("No data to export");
      return;
    }

    const toNumber = (value) => {
      const parsed = Number(value);
      return Number.isFinite(parsed) ? parsed : 0;
    };

    const parseReportDate = (value) => {
      if (!value) return Number.MAX_SAFE_INTEGER;
      const parsed = Date.parse(value);
      return Number.isNaN(parsed) ? Number.MAX_SAFE_INTEGER : parsed;
    };

    const toExcelDateSerial = (value) => {
      if (!value) return "-";
      const serial = utility.convertDateToExcelSerial(value);
      return serial !== null ? serial : "-";
    };

    const wb = XLSX.utils.book_new();
    const ws = {};
    ws["!merges"] = [];

    // Filter labels
    let fromCompanyLabel = "All";
    if (fromCompanyId) {
      const found = companies.find((c) => String(c.id) === String(fromCompanyId));
      fromCompanyLabel = found?.companyName || fromCompanyId;
    }
    let toCompanyLabel = "All";
    if (toCompanyId) {
      const found = companies.find((c) => String(c.id) === String(toCompanyId));
      toCompanyLabel = found?.companyName || toCompanyId;
    }
    let fromProjectLabel = "All";
    if (fromProjectId) {
      const found = fromProjects.find((p) => String(p.projectId || p.id) === String(fromProjectId));
      fromProjectLabel = formatProjectDisplay(found || fromProjectId);
    }
    let toProjectLabel = "All";
    if (toProjectId) {
      const found = toProjects.find((p) => String(p.projectId || p.id) === String(toProjectId));
      toProjectLabel = formatProjectDisplay(found || toProjectId);
    }
    let fromStoreLabel = "All";
    if (fromStoreId) {
      const found = fromStores.find((s) => String(s.storeId || s.id) === String(fromStoreId));
      fromStoreLabel = found?.storeName || fromStoreId;
    }
    let toStoreLabel = "All";
    if (toStoreId) {
      const found = toStores.find((s) => String(s.storeId || s.id) === String(toStoreId));
      toStoreLabel = found?.storeName || toStoreId;
    }
    let itemCategoryLabel = "All";
    if (itemCategoryId) {
      const found = itemCategories.find((c) => String(c.id) === String(itemCategoryId));
      itemCategoryLabel = found?.itemCategoryName || itemCategoryId;
    }
    let itemLabel = "All";
    if (itemId) {
      const found = items.find((i) => String(i.id) === String(itemId));
      itemLabel = found?.itemName || found?.itemCode || itemId;
    }
    let statusLabel = status === "ALL" ? "All" : status.toUpperCase();

    let row = 0;
    const companyHeader = activeCompany?.companyName || "Company";
    const thin = { style: "thin" };
    const center = { horizontal: "center", vertical: "center", wrapText: true };

    const reportTitleText =
      reportMode === "TRANSFER"
        ? "Stock Transfer (Outward) Report"
        : "Stock Receive (Inward) Report";

    // COMPANY NAME (Top Left) & REPORT DATE (Top Right)
    const headerReportDateStr = `Report Date: ${utility.formatExportDateTime(new Date())}`;
    XLSX.utils.sheet_add_aoa(
      ws,
      [[companyHeader, "", "", "", "", "", "", "", "", "", "", headerReportDateStr, "", "", "", "", "", "", ""]],
      { origin: { r: row, c: 0 } }
    );
    ws["!merges"].push({ s: { r: row, c: 0 }, e: { r: row, c: 10 } });
    ws["!merges"].push({ s: { r: row, c: 11 }, e: { r: row, c: 18 } });
    ws[XLSX.utils.encode_cell({ r: row, c: 0 })].s = {
      font: { bold: true, color: { rgb: "000000" }, sz: 12 },
      alignment: { horizontal: "left", vertical: "center" },
    };
    ws[XLSX.utils.encode_cell({ r: row, c: 11 })].s = {
      font: { bold: true, color: { rgb: "000000" }, sz: 10 },
      alignment: { horizontal: "right", vertical: "center" },
    };

    row++;

    // TITLE
    XLSX.utils.sheet_add_aoa(ws, [[reportTitleText]], { origin: { r: row, c: 0 } });
    ws["!merges"].push({ s: { r: row, c: 0 }, e: { r: row, c: 18 } });
    ws[XLSX.utils.encode_cell({ r: row, c: 0 })].s = {
      font: { bold: true, sz: 16 },
      alignment: { horizontal: "center", vertical: "center" },
    };

    row += 2;

    let transferTypeLabel = "All";
    if (transferType === "INTERNAL") {
      transferTypeLabel = "Internal Transfer";
    } else if (transferType === "COMPANY") {
      transferTypeLabel = "External Transfer";
    }

    // FILTER INFORMATION
    XLSX.utils.sheet_add_aoa(
      ws,
      [
        ["Report Mode:", reportMode === "TRANSFER" ? "Stock Transfer (Outward)" : "Stock Receive (Inward)", "", "Status:", statusLabel, "", "Transfer Type:", transferTypeLabel],
        ["From Company:", fromCompanyLabel, "", "From Project:", fromProjectLabel, "", "From Store:", fromStoreLabel],
        ["To Company:", toCompanyLabel, "", "To Project:", toProjectLabel, "", "To Store:", toStoreLabel],
        ["Item Category:", itemCategoryLabel, "", "Item Name:", itemLabel, "", "From Date:", startDate ? utility.formatDateDDMMMYYYY(startDate) : "-"],
        ["To Date:", endDate ? utility.formatDateDDMMMYYYY(endDate) : "-", "", "", "", "", "", ""],
      ],
      { origin: { r: row, c: 0 } }
    );

    const filterLabelStyle = {
      font: { bold: true, sz: 10 },
      alignment: { horizontal: "left", vertical: "center" },
      border: { top: thin, bottom: thin, left: thin, right: thin },
    };
    const filterValueStyle = {
      font: { sz: 10 },
      alignment: { horizontal: "left", vertical: "center" },
      border: { top: thin, bottom: thin, left: thin, right: thin },
    };
    const boldLabelColumns = [0, 3, 6];
    for (let i = 0; i < 5; i++) {
      for (let c = 0; c < 8; c++) {
        const cell = XLSX.utils.encode_cell({ r: row + i, c });
        if (!ws[cell]) continue;
        if (boldLabelColumns.includes(c) || (i === 4 && c === 0)) {
          ws[cell].s = filterLabelStyle;
        } else {
          ws[cell].s = filterValueStyle;
        }
      }
    }
    row += 6;

    // TABLE HEADER
    const headers = [
      "Sr.No",
      "Date",
      "From Company",
      "From Project",
      "From Store",
      "To Company",
      "To Project",
      "To Store",
      "Transporter",
      "Voucher No",
      "Item Category",
      "Item Code / Name",
      "Item Desc",
      "UOM",
      "Transfer Qty",
      "Received Qty",
      "Damaged Qty",
      "Lost Qty",
      "Issued By",
      "Received By",
      "Status",
    ];

    XLSX.utils.sheet_add_aoa(ws, [headers], { origin: { r: row, c: 0 } });
    headers.forEach((_, colIndex) => {
      const cell = XLSX.utils.encode_cell({ r: row, c: colIndex });
      ws[cell].s = {
        font: { bold: true, sz: 10 },
        alignment: center,
        border: { top: thin, bottom: thin, left: thin, right: thin },
      };
    });

    row++;

    // TABLE DATA
    allData.forEach((reportRow, index) => {
      const rowData = [
        index + 1,
        toExcelDateSerial(reportRow.date),
        reportRow.fromCompany || "-",
        formatProjectDisplay({
          projectName: reportRow.fromProject,
          projectCategoryName: reportRow.fromProjectCategoryName,
          projectCode: reportRow.fromProjectCode,
        }),
        reportRow.fromStore || "-",
        reportRow.toCompany || "-",
        formatProjectDisplay({
          projectName: reportRow.toProject,
          projectCategoryName: reportRow.toProjectCategoryName,
          projectCode: reportRow.toProjectCode,
        }),
        reportRow.toStore || "-",
        reportRow.transporter || "-",
        reportRow.voucherNo || "-",
        reportRow.itemCategory || "-",
        reportRow.itemName || reportRow.itemCode || "-",
        reportRow.itemDescription || "-",
        reportRow.uom || "-",
        toNumber(reportRow.quantity),
        toNumber(reportRow.receivedQuantity),
        toNumber(reportRow.damagedQuantity) > 0 && reportRow.scrapStoreName
          ? `${toNumber(reportRow.damagedQuantity).toFixed(3)} (${reportRow.scrapStoreName})`
          : toNumber(reportRow.damagedQuantity),
        toNumber(reportRow.lostQuantity),
        reportRow.issuedBy || "-",
        reportRow.receivedBy || "-",
        (reportRow.status || "-").toUpperCase(),
      ];

      XLSX.utils.sheet_add_aoa(ws, [rowData], { origin: { r: row, c: 0 } });
      rowData.forEach((_, colIndex) => {
        const cell = XLSX.utils.encode_cell({ r: row, c: colIndex });
        const isNumeric = colIndex === 0 || (colIndex >= 14 && colIndex <= 17 && typeof ws[cell].v === "number");
        const isCenter = colIndex === 0 || colIndex === 1 || colIndex === 13 || colIndex === 20;
        ws[cell].s = {
          font: { sz: 10 },
          alignment: {
            horizontal: isNumeric ? "right" : (isCenter ? "center" : "left"),
            vertical: "center",
          },
          border: { top: thin, bottom: thin, left: thin, right: thin },
        };

        if (colIndex === 0) {
          ws[cell].t = "n";
          ws[cell].z = "0";
        }
        if (colIndex === 1 && typeof ws[cell].v === "number") {
          ws[cell].t = "n";
          ws[cell].z = "dd-mm-yyyy";
        }
        if (colIndex >= 14 && colIndex <= 17) {
          if (typeof ws[cell].v === "number") {
            ws[cell].t = "n";
            ws[cell].z = "#,##0.000";
          } else {
            ws[cell].t = "s";
          }
        }
      });
      row++;
    });

    // TOTALS ROW
    const totalTransferQty = allData.reduce((sum, r) => sum + toNumber(r.quantity), 0);
    const totalReceivedQty = allData.reduce((sum, r) => sum + toNumber(r.receivedQuantity), 0);
    const totalDamagedQty = allData.reduce((sum, r) => sum + toNumber(r.damagedQuantity), 0);
    const totalLostQty = allData.reduce((sum, r) => sum + toNumber(r.lostQuantity), 0);

    const totalsRow = Array(21).fill("");
    totalsRow[13] = "Total";
    totalsRow[14] = totalTransferQty;
    totalsRow[15] = totalReceivedQty;
    totalsRow[16] = totalDamagedQty;
    totalsRow[17] = totalLostQty;
    XLSX.utils.sheet_add_aoa(ws, [totalsRow], { origin: { r: row, c: 0 } });

    for (let c = 0; c < 21; c++) {
      const cell = XLSX.utils.encode_cell({ r: row, c });
      const isNumeric = c >= 14 && c <= 17;
      ws[cell].s = {
        font: { bold: true, sz: 10 },
        alignment: {
          horizontal: isNumeric || c === 13 ? "right" : "left",
          vertical: "center",
        },
        border: { top: thin, bottom: thin, left: thin, right: thin },
      };
      if (isNumeric) {
        ws[cell].t = "n";
        ws[cell].v = totalsRow[c];
        ws[cell].z = "#,##0.000";
      }
    }

    // Set Column Widths
    ws["!cols"] = [
      { wch: 8 },  // Sr.No
      { wch: 14 }, // Date
      { wch: 20 }, // From Company
      { wch: 22 }, // From Project
      { wch: 18 }, // From Store
      { wch: 20 }, // To Company
      { wch: 22 }, // To Project
      { wch: 18 }, // To Store
      { wch: 18 }, // Transporter
      { wch: 22 }, // Voucher No
      { wch: 30 }, // Item Category
      { wch: 40 }, // Item Code/Name
      { wch: 24 }, // Item Desc
      { wch: 10 }, // UOM
      { wch: 14 }, // Transfer Qty
      { wch: 14 }, // Received Qty
      { wch: 14 }, // Damaged Qty
      { wch: 14 }, // Lost Qty
      { wch: 18 }, // Issued By
      { wch: 18 }, // Received By
      { wch: 14 }, // Status
    ];

    const fileName = reportMode === "TRANSFER" ? "StockTransferReport.xlsx" : "StockReceiveReport.xlsx";
    XLSX.utils.book_append_sheet(wb, ws, "Report");
    XLSX.writeFile(wb, fileName);
  };

  const exportPdf = async () => {
    const allData = await getAllReportData();
    if (!allData.length) {
      showMessage("No data to export");
      return;
    }

    let fromCompanyLabel = "All";
    if (fromCompanyId) {
      const found = companies.find((c) => String(c.id) === String(fromCompanyId));
      fromCompanyLabel = found?.companyName || fromCompanyId;
    }
    let toCompanyLabel = "All";
    if (toCompanyId) {
      const found = companies.find((c) => String(c.id) === String(toCompanyId));
      toCompanyLabel = found?.companyName || toCompanyId;
    }
    let fromProjectLabel = "All";
    if (fromProjectId) {
      const found = fromProjects.find((p) => String(p.projectId || p.id) === String(fromProjectId));
      fromProjectLabel = formatProjectDisplay(found || fromProjectId);
    }
    let toProjectLabel = "All";
    if (toProjectId) {
      const found = toProjects.find((p) => String(p.projectId || p.id) === String(toProjectId));
      toProjectLabel = formatProjectDisplay(found || toProjectId);
    }
    let fromStoreLabel = "All";
    if (fromStoreId) {
      const found = fromStores.find((s) => String(s.storeId || s.id) === String(fromStoreId));
      fromStoreLabel = found?.storeName || fromStoreId;
    }
    let toStoreLabel = "All";
    if (toStoreId) {
      const found = toStores.find((s) => String(s.storeId || s.id) === String(toStoreId));
      toStoreLabel = found?.storeName || toStoreId;
    }
    let itemCategoryLabel = "All";
    if (itemCategoryId) {
      const found = itemCategories.find((c) => String(c.id) === String(itemCategoryId));
      itemCategoryLabel = found?.itemCategoryName || itemCategoryId;
    }
    let itemLabel = "All";
    if (itemId) {
      const found = items.find((i) => String(i.id) === String(itemId));
      itemLabel = found?.itemName || found?.itemCode || itemId;
    }
    let statusLabel = status === "ALL" ? "All" : status.toUpperCase();

    const doc = new jsPDF("landscape", "mm", "a4");
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const companyHeaderName = activeCompany?.companyName || "Company";

    // Pre-convert logo to Data URL for 100% reliable rendering
    let pdfLogo = null;
    try {
      pdfLogo = await getPdfLogoDataUrl(logo);
    } catch (e) {
      console.warn("PDF logo load error:", e);
    }

    const drawHeader = (startY = 8) => {
      doc.setFontSize(12);
      doc.setFont(undefined, "bold");
      const companyNameWidth = doc.getTextWidth(companyHeaderName);
      const logoWidth = 5;
      const logoHeight = 10;
      const logoX = 14 + 6 + (companyNameWidth - logoWidth) / 2;
      const logoY = startY;

      if (pdfLogo) {
        doc.addImage(pdfLogo, "PNG", logoX, logoY, logoWidth, logoHeight);
      }

      doc.text(companyHeaderName, 14, startY + 16);
      doc.setFontSize(8);
      doc.setFont(undefined, "normal");
      doc.text(`Report Date: ${utility.formatExportDateTime(new Date())}`, pageWidth - 14, startY + 16, { align: "right" });

      const title =
        reportMode === "TRANSFER"
          ? "Stock Transfer (Outward) Report"
          : "Stock Receive (Inward) Report";
      doc.setFontSize(11);
      doc.setFont(undefined, "bold");
      const titleWidth = doc.getTextWidth(title);
      const centerX = pageWidth / 2;
      doc.text(title, centerX, startY + 27, { align: "center" });

      doc.setLineWidth(0.3);
      doc.line(centerX - titleWidth / 2, startY + 29, centerX + titleWidth / 2, startY + 29);
    };

    let yPosition = 8;
    drawHeader(yPosition);
    yPosition += 36;

    // Filter Information Headings in PDF
    doc.setFontSize(8);

    let xLeft1 = 14;
    let xLeft2 = 85;
    let xLeft3 = 155;
    let xLeft4 = 225;

    let transferTypeLabel = "All";
    if (transferType === "INTERNAL") {
      transferTypeLabel = "Internal Transfer";
    } else if (transferType === "COMPANY") {
      transferTypeLabel = "External Transfer";
    }

    // Row 1
    doc.setFont(undefined, "bold");
    doc.text("Report Mode:", xLeft1, yPosition);
    doc.setFont(undefined, "normal");
    doc.text(reportMode === "TRANSFER" ? "Stock Transfer (Outward)" : "Stock Receive (Inward)", xLeft1 + 20, yPosition);

    doc.setFont(undefined, "bold");
    doc.text("Status:", xLeft2, yPosition);
    doc.setFont(undefined, "normal");
    doc.text(String(statusLabel), xLeft2 + 12, yPosition);

    doc.setFont(undefined, "bold");
    doc.text("Transfer Type:", xLeft3, yPosition);
    doc.setFont(undefined, "normal");
    doc.text(String(transferTypeLabel), xLeft3 + 22, yPosition);

    doc.setFont(undefined, "bold");
    doc.text("From Date:", xLeft4, yPosition);
    doc.setFont(undefined, "normal");
    doc.text(startDate ? utility.formatDateDDMMMYYYY(startDate) : "-", xLeft4 + 17, yPosition);

    yPosition += 5;

    // Row 2
    doc.setFont(undefined, "bold");
    doc.text("From Company:", xLeft1, yPosition);
    doc.setFont(undefined, "normal");
    doc.text(String(fromCompanyLabel), xLeft1 + 24, yPosition);

    doc.setFont(undefined, "bold");
    doc.text("From Project:", xLeft2, yPosition);
    doc.setFont(undefined, "normal");
    doc.text(String(fromProjectLabel), xLeft2 + 20, yPosition);

    doc.setFont(undefined, "bold");
    doc.text("From Store:", xLeft3, yPosition);
    doc.setFont(undefined, "normal");
    doc.text(String(fromStoreLabel), xLeft3 + 18, yPosition);

    doc.setFont(undefined, "bold");
    doc.text("To Date:", xLeft4, yPosition);
    doc.setFont(undefined, "normal");
    doc.text(endDate ? utility.formatDateDDMMMYYYY(endDate) : "-", xLeft4 + 14, yPosition);

    yPosition += 5;

    // Row 3
    doc.setFont(undefined, "bold");
    doc.text("To Company:", xLeft1, yPosition);
    doc.setFont(undefined, "normal");
    doc.text(String(toCompanyLabel), xLeft1 + 20, yPosition);

    doc.setFont(undefined, "bold");
    doc.text("To Project:", xLeft2, yPosition);
    doc.setFont(undefined, "normal");
    doc.text(String(toProjectLabel), xLeft2 + 18, yPosition);

    doc.setFont(undefined, "bold");
    doc.text("To Store:", xLeft3, yPosition);
    doc.setFont(undefined, "normal");
    doc.text(String(toStoreLabel), xLeft3 + 15, yPosition);

    doc.setFont(undefined, "bold");
    doc.text("Item Category:", xLeft4, yPosition);
    doc.setFont(undefined, "normal");
    doc.text(String(itemCategoryLabel), xLeft4 + 21, yPosition);

    yPosition += 5;

    // Row 4
    doc.setFont(undefined, "bold");
    doc.text("Item Name:", xLeft1, yPosition);
    doc.setFont(undefined, "normal");
    doc.text(String(itemLabel), xLeft1 + 18, yPosition);

    yPosition += 5;

    const sortedData = [...allData].sort((a, b) => new Date(a.date || 0).getTime() - new Date(b.date || 0).getTime());

    const body = sortedData.map((row, index) => [
      index + 1,
      utility.formatDateDDMMMYYYY(row.date),
      row.fromCompany || "-",
      formatProjectDisplay({
        projectName: row.fromProject,
        projectCategoryName: row.fromProjectCategoryName,
        projectCode: row.fromProjectCode,
      }),
      row.fromStore || "-",
      row.toCompany || "-",
      formatProjectDisplay({
        projectName: row.toProject,
        projectCategoryName: row.toProjectCategoryName,
        projectCode: row.toProjectCode,
      }),
      row.toStore || "-",
      row.transporter || "-",
      row.voucherNo || "-",
      row.itemCategory || "-",
      row.itemName || row.itemCode || "-",
      row.itemDescription || "-",
      row.uom || "-",
      row.quantity !== null && row.quantity !== undefined ? Number(row.quantity).toFixed(3) : "-",
      row.receivedQuantity !== null && row.receivedQuantity !== undefined ? Number(row.receivedQuantity).toFixed(3) : "0.000",
      row.damagedQuantity !== null && row.damagedQuantity !== undefined && Number(row.damagedQuantity) > 0 && row.scrapStoreName
        ? `${Number(row.damagedQuantity).toFixed(3)} (${row.scrapStoreName})`
        : (row.damagedQuantity !== null && row.damagedQuantity !== undefined ? Number(row.damagedQuantity).toFixed(3) : "0.000"),
      row.lostQuantity !== null && row.lostQuantity !== undefined ? Number(row.lostQuantity).toFixed(3) : "0.000",
      row.issuedBy || "-",
      row.receivedBy || "-",
      (row.status || "-").toUpperCase(),
    ]);

    const totalQty = sortedData.reduce((sum, row) => sum + Number(row.quantity || 0), 0);
    const totalReceivedQty = sortedData.reduce((sum, row) => sum + Number(row.receivedQuantity || 0), 0);
    const totalDamagedQty = sortedData.reduce((sum, row) => sum + Number(row.damagedQuantity || 0), 0);
    const totalLostQty = sortedData.reduce((sum, row) => sum + Number(row.lostQuantity || 0), 0);

    autoTable(doc, {
      startY: yPosition,
      margin: { top: 40, left: 8, right: 8, bottom: 20 },
      head: [
        [
          "#",
          "Date",
          "From Co.",
          "From Proj",
          "From Store",
          "To Co.",
          "To Proj",
          "To Store",
          "Transporter",
          "Voucher No",
          "Category",
          "Item",
          "Desc",
          "UOM",
          "Transfer Qty",
          "Received Qty",
          "Damaged Qty",
          "Lost Qty",
          "Issued By",
          "Received By",
          "Status",
        ],
      ],
      body,
      foot: [
        [
          "",
          "",
          "",
          "",
          "",
          "",
          "",
          "",
          "",
          "",
          "",
          "",
          "",
          "Total",
          totalQty.toFixed(3),
          totalReceivedQty.toFixed(3),
          totalDamagedQty.toFixed(3),
          totalLostQty.toFixed(3),
          "",
          "",
          "",
        ],
      ],
      showFoot: "lastPage",
      theme: "grid",
      styles: {
        fontSize: 6,
        lineColor: [0, 0, 0],
        lineWidth: 0.15,
        fillColor: [255, 255, 255],
        textColor: [0, 0, 0],
        cellPadding: 1,
      },
      alternateRowStyles: { fillColor: [255, 255, 255] },
      headStyles: { fillColor: [230, 230, 230], textColor: [0, 0, 0], fontStyle: "bold", halign: "center" },
      footStyles: { fillColor: [255, 255, 255], textColor: [0, 0, 0], fontStyle: "bold" },
      columnStyles: {
        0: { halign: "center", cellWidth: 7 },
        1: { halign: "center", cellWidth: 16 },
        2: { cellWidth: 14 },
        3: { cellWidth: 15 },
        4: { cellWidth: 13 },
        5: { cellWidth: 14 },
        6: { cellWidth: 15 },
        7: { cellWidth: 13 },
        8: { cellWidth: 14 },
        9: { halign: "center", cellWidth: 16 },
        10: { cellWidth: 13 },
        11: { cellWidth: 13 },
        12: { cellWidth: 15 },
        13: { halign: "center", cellWidth: 9 },
        14: { halign: "right", cellWidth: 14 },
        15: { halign: "right", cellWidth: 14 },
        16: { halign: "right", cellWidth: 14 },
        17: { halign: "right", cellWidth: 14 },
        18: { cellWidth: 14 },
        19: { cellWidth: 14 },
        20: { halign: "center", cellWidth: 15 },
      },
      didDrawPage: function (data) {
        if (data.pageNumber === 1) return;
        drawHeader(6);
      },
    });

    const pageCount = doc.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      if (i === pageCount) {
        doc.setFont(undefined, "bold");
        doc.setFontSize(8);
        const footerTop = pageHeight - 15;
        doc.text("Prepared By", 20, footerTop);
        doc.text("Checked By", pageWidth / 2, footerTop, { align: "center" });
        doc.text("Approved By", pageWidth - 20, footerTop, { align: "right" });
      }
      doc.setFontSize(8);
      doc.setFont(undefined, "normal");
      doc.text(`Page ${i} of ${pageCount}`, pageWidth / 2, pageHeight - 6, { align: "center" });
    }

    const pdfFileName = reportMode === "TRANSFER" ? "StockTransferReport.pdf" : "StockReceiveReport.pdf";
    doc.save(pdfFileName);
  };

  const renderStatusBadge = (statusVal) => {
    if (!statusVal) return <span className="badge bg-secondary">-</span>;
    const s = String(statusVal).toLowerCase().trim();
    let bg = "#6c757d";
    let color = "#fff";
    let label = statusVal;

    if (s === "intransit" || s === "in-transit") {
      bg = "#ffab00";
      color = "#000";
      label = "In-Transit";
    } else if (s === "received") {
      bg = "#2ca87f";
      color = "#fff";
      label = "Received";
    } else if (s === "cancelled") {
      bg = "#343a40";
      color = "#fff";
      label = "Cancelled";
    } else if (s === "rejected") {
      bg = "#dc3545";
      color = "#fff";
      label = "Rejected";
    } else {
      label = statusVal.charAt(0).toUpperCase() + statusVal.slice(1);
    }

    return (
      <span
        style={{
          backgroundColor: bg,
          color: color,
          padding: "4px 10px",
          borderRadius: "20px",
          fontSize: "11px",
          fontWeight: "600",
          display: "inline-block",
          minWidth: "75px",
          textAlign: "center"
        }}
      >
        {label}
      </span>
    );
  };

  return (
    <>
      <Breadcrumb pageTitle={reportMode === "TRANSFER" ? "Stock Transfer Report" : "Stock Receive Report"} secondTitle="Reports" />

      <MessageDialog
        message={message}
        type={msgType}
        setMessage={setMessage}
        setMsgType={setMsgType}
      />

      <div className="card p-4 bg-white shadow-sm">
        {/* Top Report Type Radio Selector */}
        <div className="d-flex align-items-center gap-4 mb-4 pb-3 border-bottom">
          <label className="form-label fw-bold mb-0">Report Type:</label>
          <div className="form-check form-check-inline mb-0">
            <input
              className="form-check-input"
              type="radio"
              name="reportModeRadio"
              id="modeTransfer"
              checked={reportMode === "TRANSFER"}
              onChange={() => handleReportModeChange("TRANSFER")}
            />
            <label className="form-check-label fw-semibold" htmlFor="modeTransfer">
              Stock Transfer (Outward)
            </label>
          </div>
          <div className="form-check form-check-inline mb-0">
            <input
              className="form-check-input"
              type="radio"
              name="reportModeRadio"
              id="modeReceive"
              checked={reportMode === "RECEIVE"}
              onChange={() => handleReportModeChange("RECEIVE")}
            />
            <label className="form-check-label fw-semibold" htmlFor="modeReceive">
              Stock Receive (Inward)
            </label>
          </div>
        </div>

        {/* Filter Controls */}
        <div className="row g-3 mb-4">
          <div className="col-md-3">
            <label className="form-label">Transfer Type</label>
            <select
              className="form-select"
              value={transferType}
              onChange={(e) => setTransferType(e.target.value)}
            >
              <option value="">All</option>
              <option value="INTERNAL">Internal Transfer (Inter-Project)</option>
              <option value="COMPANY">External Transfer (Inter-Company)</option>
            </select>
          </div>

          <div className="col-md-3">
            <label className="form-label">Status</label>
            <select
              className="form-select"
              value={status}
              onChange={(e) => setStatus(e.target.value)}
            >
              <option value="ALL">All Statuses</option>
              <option value="intransit">In-Transit</option>
              <option value="received">Received</option>
              <option value="cancelled">Cancelled</option>
              <option value="rejected">Rejected</option>
            </select>
          </div>

          {reportMode === "TRANSFER" ? (
            <>
              <div className="col-md-3">
                <label className="form-label">
                  From Company <span className="text-danger">*</span>
                </label>
                <select
                  className="form-select"
                  value={fromCompanyId}
                  onChange={(e) => setFromCompanyId(e.target.value)}
                  disabled={Number(roleId) !== 1}
                >
                  {companies.map((company) => (
                    <option key={company.id} value={company.id}>
                      {company.companyName}
                    </option>
                  ))}
                </select>
              </div>

              <div className="col-md-3">
                <label className="form-label">From Project</label>
                <select
                  className="form-select"
                  value={fromProjectId}
                  onChange={(e) => setFromProjectId(e.target.value)}
                >
                  <option value="">All</option>
                  {fromProjects.map((project) => (
                    <option key={project.id || project.projectId} value={project.id || project.projectId}>
                      {formatProjectDisplay(project)}
                    </option>
                  ))}
                </select>
              </div>

              <div className="col-md-3">
                <label className="form-label">From Store</label>
                <select
                  className="form-select"
                  value={fromStoreId}
                  onChange={(e) => setFromStoreId(e.target.value)}
                  disabled={!fromProjectId}
                >
                  <option value="">All</option>
                  {fromStores.map((store) => (
                    <option key={store.storeId || store.id} value={store.storeId || store.id}>
                      {store.storeName}
                    </option>
                  ))}
                </select>
              </div>

              <div className="col-md-3">
                <label className="form-label">To Company</label>
                <select
                  className="form-select"
                  value={toCompanyId}
                  onChange={(e) => setToCompanyId(e.target.value)}
                  disabled={transferType === "INTERNAL"}
                >
                  <option value="">All</option>
                  {companies
                    .filter((company) =>
                      transferType === "COMPANY" ? String(company.id) !== String(fromCompanyId) : true
                    )
                    .map((company) => (
                      <option key={company.id} value={company.id}>
                        {company.companyName}
                      </option>
                    ))}
                </select>
              </div>

              <div className="col-md-3">
                <label className="form-label">To Project</label>
                <select
                  className="form-select"
                  value={toProjectId}
                  onChange={(e) => setToProjectId(e.target.value)}
                  disabled={!toCompanyId}
                >
                  <option value="">All</option>
                  {toProjects.map((project) => (
                    <option key={project.id || project.projectId} value={project.id || project.projectId}>
                      {formatProjectDisplay(project)}
                    </option>
                  ))}
                </select>
              </div>

              <div className="col-md-3">
                <label className="form-label">To Store</label>
                <select
                  className="form-select"
                  value={toStoreId}
                  onChange={(e) => setToStoreId(e.target.value)}
                  disabled={!toProjectId}
                >
                  <option value="">All</option>
                  {toStores.map((store) => (
                    <option key={store.storeId || store.id} value={store.storeId || store.id}>
                      {store.storeName}
                    </option>
                  ))}
                </select>
              </div>
            </>
          ) : (
            <>
              <div className="col-md-3">
                <label className="form-label">From Company (Source)</label>
                <select
                  className="form-select"
                  value={fromCompanyId}
                  onChange={(e) => setFromCompanyId(e.target.value)}
                  disabled={transferType === "INTERNAL"}
                >
                  <option value="">All</option>
                  {companies
                    .filter((company) =>
                      transferType === "COMPANY" ? String(company.id) !== String(toCompanyId) : true
                    )
                    .map((company) => (
                      <option key={company.id} value={company.id}>
                        {company.companyName}
                      </option>
                    ))}
                </select>
              </div>

              <div className="col-md-3">
                <label className="form-label">From Project (Source Project)</label>
                <select
                  className="form-select"
                  value={fromProjectId}
                  onChange={(e) => setFromProjectId(e.target.value)}
                  disabled={!fromCompanyId}
                >
                  <option value="">All</option>
                  {fromProjects.map((project) => (
                    <option key={project.id || project.projectId} value={project.id || project.projectId}>
                      {formatProjectDisplay(project)}
                    </option>
                  ))}
                </select>
              </div>

              <div className="col-md-3">
                <label className="form-label">From Store (Source Store)</label>
                <select
                  className="form-select"
                  value={fromStoreId}
                  onChange={(e) => setFromStoreId(e.target.value)}
                  disabled={!fromProjectId}
                >
                  <option value="">All</option>
                  {fromStores.map((store) => (
                    <option key={store.storeId || store.id} value={store.storeId || store.id}>
                      {store.storeName}
                    </option>
                  ))}
                </select>
              </div>

              <div className="col-md-3">
                <label className="form-label">
                  To Company (Receiving) <span className="text-danger">*</span>
                </label>
                <select
                  className="form-select"
                  value={toCompanyId}
                  onChange={(e) => setToCompanyId(e.target.value)}
                  disabled={Number(roleId) !== 1}
                >
                  {companies.map((company) => (
                    <option key={company.id} value={company.id}>
                      {company.companyName}
                    </option>
                  ))}
                </select>
              </div>

              <div className="col-md-3">
                <label className="form-label">To Project (Receiving Site)</label>
                <select
                  className="form-select"
                  value={toProjectId}
                  onChange={(e) => setToProjectId(e.target.value)}
                >
                  <option value="">All</option>
                  {toProjects.map((project) => (
                    <option key={project.id || project.projectId} value={project.id || project.projectId}>
                      {formatProjectDisplay(project)}
                    </option>
                  ))}
                </select>
              </div>

              <div className="col-md-3">
                <label className="form-label">To Store (Receiving Store)</label>
                <select
                  className="form-select"
                  value={toStoreId}
                  onChange={(e) => setToStoreId(e.target.value)}
                  disabled={!toProjectId}
                >
                  <option value="">All</option>
                  {toStores.map((store) => (
                    <option key={store.storeId || store.id} value={store.storeId || store.id}>
                      {store.storeName}
                    </option>
                  ))}
                </select>
              </div>
            </>
          )}

          <div className="col-md-3">
            <label className="form-label">Item Category</label>
            <select
              className="form-select"
              value={itemCategoryId}
              onChange={(e) => setItemCategoryId(e.target.value)}
            >
              <option value="">All</option>
              {itemCategories.map((cat) => (
                <option key={cat.id} value={cat.id}>
                  {cat.itemCategoryName}
                </option>
              ))}
            </select>
          </div>

          <div className="col-md-3">
            <label className="form-label">Item Name</label>
            <select
              className="form-select"
              value={itemId}
              onChange={(e) => setItemId(e.target.value)}
              disabled={!itemCategoryId}
            >
              <option value="">All</option>
              {items.map((item) => (
                <option key={item.id} value={item.id}>
                  {item.itemName} ({item.itemCode})
                </option>
              ))}
            </select>
          </div>

          <div className="col-md-3">
            <label className="form-label">From Date</label>
            <input
              type="date"
              className="form-control"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
          </div>

          <div className="col-md-3">
            <label className="form-label">To Date</label>
            <input
              type="date"
              className="form-control"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
            />
          </div>

          <div className="col-12 d-flex justify-content-end gap-2 mt-3">
            <button className="btn btn-secondary" onClick={handleClearFilters}>
              <i className="ti ti-refresh me-1"></i>
              Clear
            </button>
            <button className="btn btn-primary" onClick={() => loadReport(1)} disabled={loading}>
              <i className="ti ti-search me-1"></i>
              {loading ? "Searching..." : "View Report"}
            </button>
          </div>
        </div>

        <hr />

        {/* Export Buttons */}
        <div className="mb-3 d-flex justify-content-end align-items-center">
          <div className="d-flex gap-2">
            <button className="btn btn-success" onClick={exportPdf} disabled={loading || reports.length === 0}>
              <i className="ti ti-file-type-pdf me-1"></i>
              Export PDF
            </button>
            <button className="btn btn-success" onClick={exportExcel} disabled={loading || reports.length === 0}>
              <i className="ti ti-file-spreadsheet me-1"></i>
              Export Excel
            </button>
          </div>
        </div>

        {/* Data Table */}
        <div className="table-responsive">
          <table className="table table-bordered table-striped align-middle mb-0">
            <thead className="table-light">
              <tr>
                <th style={{ width: "50px" }}>#</th>
                <th>Date</th>
                <th>From Company</th>
                <th>From Project</th>
                <th>From Store</th>
                <th>To Company</th>
                <th>To Project</th>
                <th>To Store</th>
                <th>Transporter</th>
                <th>Voucher No</th>
                <th>Item Category</th>
                <th>Item Code / Name</th>
                <th>Item Desc</th>
                <th>UOM</th>
                <th className="text-end">Transfer Qty</th>
                <th className="text-end">Received Qty</th>
                <th className="text-end">Damaged Qty</th>
                <th className="text-end">Lost Qty</th>
                <th>Issued By</th>
                <th>Received By</th>
                <th className="text-center">Status</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="21" className="text-center py-4">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                  </td>
                </tr>
              ) : reports.length === 0 ? (
                <tr>
                  <td colSpan="21" className="text-center py-4 text-muted">
                    No data found
                  </td>
                </tr>
              ) : (
                reports.map((row, index) => (
                  <tr key={row.id || index}>
                    <td>{(page - 1) * limit + index + 1}</td>
                    <td className="text-nowrap">{utility.formatDateDDMMMYYYY(row.date)}</td>
                    <td>{row.fromCompany || "-"}</td>
                    <td>
                      {formatProjectDisplay({
                        projectName: row.fromProject,
                        projectCategoryName: row.fromProjectCategoryName,
                        projectCode: row.fromProjectCode,
                      })}
                    </td>
                    <td>{row.fromStore || "-"}</td>
                    <td>{row.toCompany || "-"}</td>
                    <td>
                      {formatProjectDisplay({
                        projectName: row.toProject,
                        projectCategoryName: row.toProjectCategoryName,
                        projectCode: row.toProjectCode,
                      })}
                    </td>
                    <td>{row.toStore || "-"}</td>
                    <td>{row.transporter || "-"}</td>
                    <td className="text-nowrap">{row.voucherNo || "-"}</td>
                    <td>{row.itemCategory || "-"}</td>
                    <td>{row.itemName || row.itemCode || "-"}</td>
                    <td>{row.itemDescription || "-"}</td>
                    <td>{row.uom || "-"}</td>
                    <td className="text-end">
                      {row.quantity !== null && row.quantity !== undefined ? Number(row.quantity).toFixed(3) : "-"}
                    </td>
                    <td className="text-end">
                      {row.receivedQuantity !== null && row.receivedQuantity !== undefined
                        ? Number(row.receivedQuantity).toFixed(3)
                        : "0.000"}
                    </td>
                    <td className="text-end">
                      {row.damagedQuantity !== null && row.damagedQuantity !== undefined
                        ? Number(row.damagedQuantity) > 0 && row.scrapStoreName
                          ? `${Number(row.damagedQuantity).toFixed(3)} (${row.scrapStoreName})`
                          : Number(row.damagedQuantity).toFixed(3)
                        : "0.000"}
                    </td>
                    <td className="text-end">
                      {row.lostQuantity !== null && row.lostQuantity !== undefined
                        ? Number(row.lostQuantity).toFixed(3)
                        : "0.000"}
                    </td>
                    <td>{row.issuedBy || "-"}</td>
                    <td>{row.receivedBy || "-"}</td>
                    <td className="text-center text-nowrap">
                      {renderStatusBadge(row.status)}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="d-flex justify-content-between align-items-center mt-3">
              <div>
                Total Records: <strong>{totalRecords}</strong>
              </div>
              <div className="d-flex gap-2">
                <button
                  className="btn btn-sm btn-secondary"
                  disabled={page === 1}
                  onClick={() => loadReport(page - 1)}
                >
                  Prev
                </button>
                {[...Array(totalPages)]
                  .map((_, i) => i + 1)
                  .filter((p) => p >= page - 2 && p <= page + 2)
                  .map((p) => (
                    <button
                      key={p}
                      className={`btn btn-sm ${page === p ? "btn-primary" : "btn-outline-primary"}`}
                      onClick={() => loadReport(p)}
                    >
                      {p}
                    </button>
                  ))}
                <button
                  className="btn btn-sm btn-secondary"
                  disabled={page === totalPages}
                  onClick={() => loadReport(page + 1)}
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
